import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Edit3, Share2, Download, RefreshCw, Star, LayoutDashboard,
  Clock, Users, Globe, Lock, Layers, ChevronRight, Folder, Home,
} from 'lucide-react';
import { dashboardsApi } from '../api/dashboardsApi';
import { invalidateQueries } from '../../analytics-shared/hooks/queryShim';
import DashboardCanvas from '../components/canvas/DashboardCanvas';
import DashboardFilterBar from '../components/filters/DashboardFilterBar';
import DrillDownModal from '../components/modals/DrillDownModal';

import ShareModal from '../../analytics-shared/components/ShareModal';
import DashboardExportModal from '../../analytics-shared/components/DashboardExportModal';
import { ASSET_DASHBOARD } from '../../analytics-shared/api/sharesApi';

const VIS_ICON = { Private: Lock, Shared: Users, Team: Users, Public: Globe };
const VIS_CHIP = {
  Private: 'bg-slate-100 text-slate-500 border-slate-200',
  Shared: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  Team: 'bg-blue-50 text-blue-600 border-blue-200',
  Public: 'bg-violet-50 text-violet-600 border-violet-200',
};

function timeSince(iso) {
  if (!iso) return '';
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (h < 1) return 'Just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d < 30 ? `${d}d ago` : `${Math.floor(d / 30)}mo ago`;
}

const DashboardViewerPage = () => {
  const { dashboardId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [dashboard, setDashboard] = useState(null);
  const [filters, setFilters] = useState({ datePreset: 'all', owner: 'All Owners' });
  const [drillDown, setDrillDown] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const d = await dashboardsApi.get(dashboardId);
      if (cancelled) return;
      // Paint the dashboard immediately. markViewed was previously awaited
      // *before* setDashboard, so the whole view blocked on a write request
      // that the user doesn't care about — the main cause of the slow
      // "View / Preview" load. Fire it as a background, non-blocking call.
      setDashboard(d || null);
      if (d) dashboardsApi.markViewed(dashboardId).catch(() => {});
    })();
    return () => { cancelled = true; };
  }, [dashboardId]);

  // Deep-link from the list page's "Download…" menu lands here with
  // ?download=1, then auto-opens the export modal once the dashboard
  // (and a quick render pass for the widgets) is on screen. The
  // ~500ms delay gives Recharts a tick to mount its SVGs so the
  // first capture isn't a half-painted canvas.
  useEffect(() => {
    if (!dashboard) return undefined;
    if (searchParams.get('download') !== '1') return undefined;
    const t = setTimeout(() => setExportOpen(true), 500);
    return () => clearTimeout(t);
  }, [dashboard, searchParams]);

  const handleFavorite = useCallback(async () => {
    if (!dashboard) return;
    await dashboardsApi.toggleFavorite(dashboard.id);
    setDashboard((prev) => prev ? { ...prev, favorite: !prev.favorite } : prev);
    // Keep the list page's Favorites tab/count in sync without a reload.
    invalidateQueries(['dashboards']);
  }, [dashboard]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    setTimeout(() => {
      setRefreshKey((k) => k + 1);
      setRefreshing(false);
    }, 600);
  }, []);

  if (dashboard === null) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-[#f4f5f8] text-center p-8">
        <div className="w-20 h-20 rounded-3xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-5">
          <LayoutDashboard className="w-10 h-10 text-indigo-200" />
        </div>
        <p className="text-lg font-bold text-slate-700">Dashboard not found</p>
        <p className="text-sm text-slate-400 mt-1 max-w-xs">
          It may have been deleted or you don&apos;t have access to this dashboard.
        </p>
        <button onClick={() => navigate('/reports/dashboards')}
          className="mt-6 flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold rounded-xl hover:from-indigo-700 hover:to-violet-700 transition-all shadow-sm shadow-indigo-200">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboards
        </button>
      </div>
    );
  }

  const VIcon = VIS_ICON[dashboard.visibility] || Lock;
  const vChipCls = VIS_CHIP[dashboard.visibility] || VIS_CHIP.Private;
  const widgetCount = dashboard.widgets?.length || 0;

  return (
    // The export anchor (`data-dashboard-export-root`) is NOT on this
    // wrapper — wrapping the whole page would capture the topbar,
    // breadcrumb and filter bar (Salesforce-style export must be the
    // dashboard canvas only). It lives inside DashboardCanvas on the
    // react-grid-layout wrapper, which has natural full layout
    // height even when widgets scroll off-screen.
    <div className="flex flex-col h-full bg-[#f4f5f8] overflow-hidden">
      <div className="flex-shrink-0 flex items-center gap-2 px-4 h-12 bg-white border-b border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.06)] z-20">
        <button onClick={() => navigate('/crm-platform')}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-indigo-600 hover:bg-slate-50 px-2 py-1 rounded-lg transition-colors flex-shrink-0"
          title="Back to CRM">
          <Home className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">CRM</span>
        </button>
        <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
        <button onClick={() => navigate('/reports/dashboards')}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-700 transition-colors group flex-shrink-0">
          <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
          <span className="hidden sm:inline">Dashboards</span>
        </button>
        <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-6 h-6 rounded-md bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <LayoutDashboard className="w-3.5 h-3.5 text-indigo-600" />
          </div>
          <h1 className="text-sm font-bold text-slate-800 truncate">{dashboard.name}</h1>
          <span className={`hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border flex-shrink-0 ${vChipCls}`}>
            <VIcon className="w-2.5 h-2.5" />
            {dashboard.visibility}
          </span>
        </div>

        <div className="hidden lg:flex items-center gap-3 text-[11px] text-slate-400 flex-shrink-0">
          {dashboard.folder && (
            <span className="flex items-center gap-1"><Folder className="w-3 h-3" />{dashboard.folder}</span>
          )}
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{timeSince(dashboard.updatedAt)}</span>
          <span className="flex items-center gap-1"><Layers className="w-3 h-3" />{widgetCount} widget{widgetCount !== 1 ? 's' : ''}</span>
        </div>

        <div className="w-px h-5 bg-slate-100 flex-shrink-0 mx-1" />

        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button onClick={handleFavorite}
            className={`p-1.5 rounded-lg transition-colors ${
              dashboard.favorite ? 'text-amber-400 bg-amber-50' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50'
            }`} title={dashboard.favorite ? 'Remove favorite' : 'Add to favorites'}>
            <Star className={`w-3.5 h-3.5 ${dashboard.favorite ? 'fill-amber-400' : ''}`} />
          </button>
          <button onClick={handleRefresh}
            className={`p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all ${refreshing ? 'animate-spin' : ''}`} title="Refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setShareOpen(true)}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            title="Share"
          >
            <Share2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setExportOpen(true)}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            title="Download"
          >
            <Download className="w-3.5 h-3.5" />
          </button>

          <div className="w-px h-4 bg-slate-200 mx-1.5" />

          <button onClick={() => navigate(`/reports/dashboards/${dashboard.id}/edit`)}
            className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg hover:from-indigo-700 hover:to-violet-700 transition-all shadow-sm shadow-indigo-200">
            <Edit3 className="w-3 h-3" />
            Edit
          </button>
        </div>
      </div>

      <DashboardFilterBar filters={filters} onChange={setFilters} />

      <div className="flex-1 overflow-hidden">
        {widgetCount === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <div className="relative mb-6">
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-100 flex items-center justify-center shadow-sm">
                <LayoutDashboard className="w-12 h-12 text-indigo-300" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-amber-100 border-2 border-white flex items-center justify-center">
                <span className="text-amber-500 text-sm font-bold">0</span>
              </div>
            </div>
            <p className="text-base font-bold text-slate-700">This dashboard has no widgets yet</p>
            <p className="text-sm text-slate-400 mt-1.5 max-w-sm">
              Open the builder and add saved reports as widgets to populate your dashboard.
            </p>
            <button onClick={() => navigate(`/reports/dashboards/${dashboard.id}/edit`)}
              className="mt-5 flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold rounded-xl hover:from-indigo-700 hover:to-violet-700 transition-all shadow-sm shadow-indigo-200">
              <Edit3 className="w-4 h-4" />
              Open Builder
            </button>
          </div>
        ) : (
          <DashboardCanvas
            key={refreshKey}
            widgets={dashboard.widgets}
            editMode={false}
            onDrillDown={setDrillDown}
            globalFilters={filters}
          />
        )}
      </div>

      <DrillDownModal drillDown={drillDown} onClose={() => setDrillDown(null)} />

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        assetType={ASSET_DASHBOARD}
        assetId={dashboard?.id}
        assetName={dashboard?.name}
        ownerName={dashboard?.createdBy}
        ownerId={dashboard?.createdById}
      />

      <DashboardExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        dashboard={dashboard}
      />
    </div>
  );
};

export default DashboardViewerPage;
